import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Check, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  pink: '#df1283',
  orange: '#FF7901',
  cyan: '#0FFFE2',
  lime: '#B3FF00',
};

const WelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const plan = searchParams.get('plan');
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [error, setError] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid checkout session. Please try again from the pricing page.');
      setLoading(false);
      setVerifying(false);
      return;
    }
    
    verifyCheckout();
  }, [sessionId]);

  const verifyCheckout = async () => {
    try {
      const response = await axios.get(`${API}/api/stripe/verify-checkout/${sessionId}`);
      setCheckoutInfo(response.data);
      
      // Pre-fill name if available
      if (response.data.name) {
        const nameParts = response.data.name.split(' ');
        setFirstName(nameParts[0] || '');
        setLastName(nameParts.slice(1).join(' ') || '');
      }
    } catch (err) {
      console.error('Checkout verification error:', err);
      setError(err.response?.data?.detail || 'Unable to verify your payment. Please contact support.');
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      // Capture UTM parameters from URL (they may have been preserved through checkout)
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content'),
        ref: urlParams.get('ref')
      };
      
      // Remove null/undefined values
      const cleanUtmParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v != null)
      );
      
      const response = await axios.post(`${API}/api/auth/guest-signup`, {
        session_id: sessionId,
        password: password,
        first_name: firstName,
        last_name: lastName,
        ...cleanUtmParams
      }, { withCredentials: true });
      
      // Set user in auth context
      setUser(response.data);
      
      // Store session token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      // Redirect to dashboard
      navigate('/dashboard?welcome=true');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: COLORS.pink }} />
          <p className="text-white/70" data-testid="welcome-verifying-status">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error && !checkoutInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-red-400 text-3xl">!</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
          <p className="text-white/60 mb-6" data-testid="welcome-fatal-error-message">{error}</p>
          <button
            data-testid="welcome-back-to-pricing-button"
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                const pricingSection = document.getElementById('pricing');
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }}
            className="px-6 py-3 rounded-full font-semibold text-white border border-white/20 hover:border-white/40 transition-all"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: `linear-gradient(135deg, ${COLORS.pink}30, ${COLORS.orange}30)` }}
          >
            <Check className="w-10 h-10" style={{ color: COLORS.lime }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
          <p className="text-white/60">
            Welcome to UnCloned® Journal Premium. Let&apos;s set up your account.
          </p>
        </div>

        {/* Account Creation Form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <div className="mb-6 p-4 rounded-lg" style={{ background: `linear-gradient(135deg, ${COLORS.cyan}10, ${COLORS.lime}10)` }}>
            <p className="text-sm text-white/70">
              <span className="font-semibold text-white">Your email:</span> {checkoutInfo?.email}
            </p>
            <p className="text-sm mt-1" style={{ color: COLORS.lime }}>
              {plan === 'yearly' ? 'Yearly Plan - $75/year' : 'Monthly Plan - $9.99/month'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">First Name</label>
                <input
                  data-testid="welcome-first-name-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2">Last Name</label>
                <input
                  data-testid="welcome-last-name-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2">Create Password</label>
              <div className="relative">
                <input
                  data-testid="welcome-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283] pr-12"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
                <button
                  data-testid="welcome-toggle-password-button"
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-2">Confirm Password</label>
              <input
                data-testid="welcome-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                placeholder="Confirm your password"
                required
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm" data-testid="welcome-error-message">{error}</p>
            )}

            <button
              data-testid="welcome-create-account-button"
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-full font-semibold text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                'Create My Account'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-white/40 text-sm mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
